// Pantallas transversales (UX §15): arranque, error de arranque, no encontrado, error de ruta, en construcción.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, isRouteErrorResponse, useNavigate, useParams, useRouteError } from 'react-router';
import { Construction, FileQuestion, RotateCw, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { AtteLogo } from '@/components/brand/AtteLogo';
import { isApiError } from '@/lib/api';
import { t } from '@/lib/i18n/es-AR';
import { PageHeader } from './PageHeader';

/** Carga de arranque (DS §6.24): wordmark + barra fina indeterminada; "Abriendo Atte…" a los 2 s. */
export function BootScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-bg" role="status" aria-live="polite">
      <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-transparent" aria-hidden>
        <div className="h-full w-2/5 animate-indeterminate bg-accent motion-reduce:animate-none" />
      </div>
      <AtteLogo height={40} variant="brand" />
      <p className={slow ? 'mt-4 text-body text-muted' : 'sr-only'}>{t.boot.opening}</p>
    </div>
  );
}

/** Error de arranque (UX §15.1): causa en lenguaje claro, "Reintentar" y reintento automático cada 10 s. */
export function BootError({ error, onRetry, retrying }: { error: unknown; onRetry: () => void; retrying?: boolean }) {
  useEffect(() => {
    const timer = setInterval(onRetry, 10_000);
    return () => clearInterval(timer);
  }, [onRetry]);
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  const cause = offline || (isApiError(error) && error.code === 'network') ? t.boot.offline : t.boot.serverDown;
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 text-center" role="alert">
      <AtteLogo height={40} variant="brand" />
      <h1 className="m-0 mt-8 text-h2 text-ink">{t.boot.failedTitle}</h1>
      <p className="m-0 mt-2 text-body text-muted">{cause}</p>
      <Button className="mt-6" variant="primary" iconStart={RotateCw} loading={retrying} loadingText="Probando…" onClick={onRetry}>
        {t.common.retry}
      </Button>
      <p className="m-0 mt-3 text-caption text-faint">{t.boot.autoRetry}</p>
    </div>
  );
}

/** Página de una feature todavía sin construir (los módulos la reemplazan). */
export function PlaceholderPage({ title, subtitle, icon = Construction, children }: { title: string; subtitle?: string; icon?: LucideIcon; children?: ReactNode }) {
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />
      <div className="rounded-md border border-border bg-surface">
        <EmptyState icon={icon} title={t.placeholder.title} description={t.placeholder.description} size="page" />
        {children}
      </div>
    </>
  );
}

/** Ruta inexistente dentro de la app (UX §15.3). */
export function NotFoundInApp() {
  const { locationSlug } = useParams();
  const navigate = useNavigate();
  return (
    <EmptyState
      size="page"
      icon={FileQuestion}
      title={t.notFound.title}
      description={t.notFound.description}
      action={{ label: t.common.goToAgenda, onClick: () => navigate(`/app/${locationSlug ?? ''}/agenda`), variant: 'primary' }}
    />
  );
}

/** Ruta inexistente fuera de la app (pantalla completa). */
export function NotFoundPage() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4">
      <Link to="/" aria-label="Atte, inicio" className="mb-6 rounded-sm">
        <AtteLogo height={32} variant="brand" />
      </Link>
      <EmptyState
        icon={FileQuestion}
        title={t.notFound.title}
        description={t.notFound.description}
        action={{ label: t.common.goToAgenda, href: '/app', variant: 'primary' }}
      />
    </div>
  );
}

/** Error de render o de carga de una ruta (500 de la SPA). Un chunk viejo después de un deploy recarga. */
export function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const chunkFailed = error instanceof Error && /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(error.message);

  useEffect(() => {
    // Después de un deploy, los chunks viejos ya no existen: una sola recarga lo resuelve.
    if (!chunkFailed) return;
    try {
      if (sessionStorage.getItem('atte.chunkReload') === '1') return;
      sessionStorage.setItem('atte.chunkReload', '1');
    } catch {
      return;
    }
    window.location.reload();
  }, [chunkFailed]);

  if (status === 404) return <NotFoundPage />;
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4" role="alert">
      <AtteLogo height={32} variant="brand" className="mb-6" />
      <EmptyState
        icon={TriangleAlert}
        title={t.serverError.title}
        description={t.serverError.description}
        action={{ label: t.serverError.reload, onClick: () => window.location.reload(), variant: 'primary' }}
        secondaryAction={{ label: t.common.back, onClick: () => navigate(-1) }}
      />
    </div>
  );
}
